/* eslint-disable no-underscore-dangle */
import React from 'react';
import PropTypes from 'prop-types';
import replaceCssClasses from '@src/js/Style';
import manager from '@src/js/TranslationManager';
import Context from './Context';
import Editor from './Editor';
import ContextHelper from './ContextHelper';

export default class AbstractComponent extends React.Component {

    constructor(props, context = {}, ss = null) {

        super(props, context, ss);

        this.state = { value: null, hasValue: false };
        this._context = null;
    }

    static get propTypes() {

        return {
            role: PropTypes.string, // var name in storage
            placeholder: PropTypes.string, // hint for user
            context: PropTypes.object, // context for container (prefix, available storages)
            storage: PropTypes.oneOfType([PropTypes.string]),
            unsafe: PropTypes.bool, // render content as raw html
            className: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.object]), // css
            children: PropTypes.any, // content
            optionalStorage: PropTypes.array,
            i18n: PropTypes.string,
            tag: PropTypes.string,
            link: PropTypes.object,
        };
    }

    static get contextType() {

        return Context;
    }

    render() {

        if (this.props.role != undefined && !this.props.placeholder)
            throw new Error('Placeholder is required');

        return this.hasContext()
            ? <Context.Provider value={ this.getContext() }>{ this.renderContent() }</Context.Provider>
            : this.renderContent();
    }

    renderContent() {

        const
            props = this.getProps(),
            Tag = this.getTag();

        return this.props.unsafe
            ? <Tag {...props} dangerouslySetInnerHTML={{ __html: this.getContent() }} />
            : <Tag {...props} children={ this.getContent() } />;
    }

    getProps() {

        const props = {
            className: replaceCssClasses(this.props.className),
            ...this.getUndeclaredProps(),
        };

        if (this.getContext()?.disabled)
            props['data-name'] = this.getRole();

        if (this.hasRole() && Editor.isEditMode())
            this.initEditor(props);

        return props;
    }

    isEmpty() {

        const value = this.getValue();

        return value === null || value === '' || value == '[]' || value == '{}';
    }

    getValue() {

        return this.state.value;
    }

    componentDidMount() {

        if (this.hasRole()) {

            Editor
                .getInstance()
                .get(this.getRole(), this.getStorageClassName(), this.getStorageId())
                .then((value) => this.updateContent(value));
        }
        else if (this.isI18n()) {

            manager
                .get(this.props.i18n)
                .then((i18n) => this.updateContent(i18n));
        }
    }

    updateContent(value) {

        this.setState({ value, hasValue: true }, () => {

            if (this.element) {

                // eslint-disable-next-line no-unused-expressions
                this.isEmpty()
                    ? this.element.classList.add('empty')
                    : this.element.classList.remove('empty');
            }
        });
    }

    getContext() {

        if (this._context == undefined)
            this._context = this.createContext();

        return this._context;
    }

    hasContext() {

        return !!(this.props.context ?? this.context ?? null);
    }

    createContext() {

        return ContextHelper.assign({ ...(this.props.context ?? this.context ?? {}) });
    }

    getContent() {

        if (this.state.hasValue)
            return this.state.value;

        return this.state.value ?? this.props.children ?? this.getSkeleton();
    }

    getSkeleton() {

        return Editor.isEditMode()
            ? 'loading...'
            : null;
    }

    hasRole() {

        return this.props.role;
    }

    initEditor(props) {

        /* eslint-disable no-param-reassign */

        if (this.getContext().disabled == undefined) {

            props['data-editable'] = 1;
            props['data-editable-type'] = 'text';
            props['data-editable-placeholder'] = this.props.placeholder;
            props['data-editable-classname'] = this.getStorageClassName();
            props['data-editable-name'] = this.getRole();
            props['data-name'] = this.getRole();
            props['data-editable-id'] = this.getStorageId();

            // legacy
            props['className'] += ' editable';
            props['data-type'] = 'plaintext';
            // props['data-popup-show'] = 'editor';
            props['data-ui-placeholder'] = this.props.placeholder;

            props['ref'] = (el) => {

                if (el) {

                    this.element = el;

                    if (el.innerText == '') {

                        el.classList.add('empty');
                        el.style.setProperty('--placeholder', `"${this.props.placeholder}"`);
                    }
                }

            };
        }
        else {

            props['data-name'] = this.getRole();
            props['data-editable-name'] = this.getRole();
            props['data-editable-type'] = 'text';
        }

        /* eslint-enable no-param-reassign */
    }

    getRole() {

        const
            context = this.getContext(),
            prefix = context?.prefix ?? context?.role ?? null,
            { role } = this.props;

        if (!role)
            return null;

        if (context?.disabled)
            return role;

        return prefix
            ? `${prefix}${role[0].toUpperCase()}${role.substring(1)}`
            : role;
    }

    getStorageByName(name) {

        const context = this.getContext();

        return context.getStorage(name);
    }

    getStorage() {

        const

            context = this.getContext(),

            storage = (this.props.storage)
                ? context.getStorage(this.props.storage)
                : context.getDefaultStorage();

        if (!storage) {

            const storages = Object.values(context.getStorages()).map((item) => item.className).join(', ');
            throw new Error(`Can't find storage ${this.props.storage} in ${storages}`);
        }

        return storage;
    }

    getStorageId() {

        return this.getStorage().id;
    }

    getStorageClassName() {

        return this.getStorage().className;
    }

    getTag() {

        return this.props.tag ?? 'div';
    }

    getUndeclaredProps() {

        const
            usedKeys = Object.keys(this.constructor.propTypes),
            filtered = Object
                .entries(this.props)
                .map(([key, value]) => {

                    if (!usedKeys.includes(key))
                        return [key, value];

                    return null;
                })
                .filter((value) => value != null);

        return Object.fromEntries(filtered ?? {});
    }

    isI18n() {

        return !!this.props.i18n;
    }

}